/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Button, Flex, Stack, Text, Tabs } from '@chakra-ui/react';

import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { ScreeningType } from '@/types/enums';
import { Job } from '@/types/Job';
import { Candidate, CandidateWithJobContext } from '@/types/Candidate';
import { Client } from '@/types/Client';
import { JobBasicInfo } from '@/types/Job';

import { useSessionForm, FormValues } from './hooks/useSessionForm';
import { useSessionData } from './hooks/useSessionData';
import { DeadlineSection } from './components/DeadlineSection';
import { SessionNameSection } from './components/SessionNameSection';
import { UpdateModeFields } from './components/UpdateModeFields';
import { HierarchicalSelection } from './components/HierarchicalSelection';
import { DirectCandidateTab } from './components/DirectCandidateTab';

export function SessionDetailsSection() {
  const navigate = useNavigate();

  // Get store methods
  const {
    screeningType,
    selectedTab,
    candidates,
    clients,
    jobs,
    client,
    job,
    candidate,
    setDeadline,
    setDeadlineTime,
    setTimezone,
    setSessionName,
    setClient,
    setJob,
    setCandidate,
    setScreeningType,
    setSelectedTab,
    setFollowUpQuestions,
    setInterviewTitle,
    setInterviewDescription,
    setQuestions,
    nextStep,
    reset: resetStore,
    isUpdateMode,
    fixedCandidate,
    fixedJobInterview,
    hasFixedJobInterviewQuestionsInitialized,
    setFixedJobInterviewQuestionsInitialized
  } = useCreateInterviewAgent();

  // Custom hooks
  const {
    form: {
      register,
      control,
      handleSubmit,
      watch,
      setValue,
      clearErrors,
      reset: resetForm,
      formState: { errors, isSubmitting }
    },
    validationRules,
    options,
    parseTimezone
  } = useSessionForm();

  const {
    isLoadingCandidate,
    error: isError,
    handleError: setIsError,
    clearError: clearIsError,
    getCandidateFollowUpQuestions,
    handleClientSelection,
    handleJobSelection,
    noCandidates
  } = useSessionData();

  // Sync form values with store changes
  useEffect(() => {
    if (selectedTab === 'hierarchical') {
      if (client) setValue('client', { value: client });
      if (job) setValue('job', { value: job as Job });
      if (candidate) setValue('candidate', { value: candidate as CandidateWithJobContext });
      setValue('directCandidate', undefined);
    } else if (selectedTab === 'direct') {
      if (candidate) setValue('directCandidate', candidate as Candidate);
      setValue('client', undefined);
      setValue('job', undefined);
      setValue('candidate', undefined);
    }
  }, [selectedTab, client, job, candidate, setValue]);

  // Fetch follow-up questions when fixedJobInterview is true (only once)
  useEffect(() => {
    const fetchFixedJobInterviewQuestions = async () => {
      // Only fetch if:
      // 1. It's a fixed job interview
      // 2. We haven't already fetched for this session (check store flag)
      // 3. Job and candidate are present
      if (fixedJobInterview && !hasFixedJobInterviewQuestionsInitialized && job && candidate) {
        try {
          const questions = await getCandidateFollowUpQuestions(
            (job as Job).job_id,
            (candidate as CandidateWithJobContext).candidate_id
          );

          setFollowUpQuestions(questions);

          const questionList = questions.flatMap((q) =>
            q.follow_up_questions.map((fq) => fq.question)
          );

          setQuestions(questionList);
          setFixedJobInterviewQuestionsInitialized(true);
        } catch (error) {
          console.error('Error fetching follow-up questions for fixed job interview:', error);
        }
      }
    };

    fetchFixedJobInterviewQuestions();
  }, [fixedJobInterview, hasFixedJobInterviewQuestionsInitialized, job, candidate]);

  // Memoized values
  const currentScreeningType = useMemo(
    () =>
      selectedTab === 'hierarchical' ? ScreeningType.JobInterview : ScreeningType.ResumeScreening,
    [selectedTab]
  );

  // Form submission handler
  const handleFormSubmit = useCallback(
    async (formData: FormValues) => {
      // Validation based on mode
      if (!isUpdateMode) {
        const isHierarchicalInvalid =
          selectedTab === 'hierarchical' && (!formData.candidate?.value || !formData.job?.value);
        const isDirectInvalid = selectedTab === 'direct' && !formData.directCandidate;

        if (isHierarchicalInvalid) {
          setIsError('Please select a candidate and job.');
          return;
        }
        if (isDirectInvalid) {
          setIsError('Please select a candidate.');
          return;
        }
      }

      try {
        // Handle follow-up questions for job interviews
        const shouldFetchQuestions =
          !isUpdateMode &&
          currentScreeningType === ScreeningType.JobInterview &&
          (job?.job_id !== formData.job?.value.job_id ||
            candidate?.candidate_id !== formData.candidate?.value.candidate_id);

        if (shouldFetchQuestions) {
          const followUpQuestions = await getCandidateFollowUpQuestions(
            formData.job!.value.job_id,
            formData.candidate!.value.candidate_id
          );

          setFollowUpQuestions(followUpQuestions);

          const questionList = followUpQuestions.flatMap((questions) =>
            questions.follow_up_questions.map((q) => q.question)
          );

          setInterviewTitle('');
          setInterviewDescription('');
          setQuestions(questionList);
        } else if (!isUpdateMode && currentScreeningType === ScreeningType.ResumeScreening) {
          setFollowUpQuestions([]);

          // Clear questions if switching from Job Interview to Resume Screening
          if (screeningType === ScreeningType.JobInterview) {
            setInterviewTitle('');
            setInterviewDescription('');
            setQuestions([]);
          }
        }

        // Update store with form data
        if (formData.deadline) setDeadline(formData.deadline);
        if (formData.deadline_time) setDeadlineTime(formData.deadline_time);
        setTimezone(formData.timezone);
        setSessionName(formData.sessionName);

        // Update client/job/candidate in create mode only
        if (!isUpdateMode) {
          setScreeningType(currentScreeningType);

          if (selectedTab === 'hierarchical') {
            setClient(formData.client?.value || null);
            setJob((formData.job?.value as Job) || null);
            setCandidate(formData.candidate?.value || null);
          } else {
            setClient(null);
            setJob(null);
            setCandidate(formData.directCandidate || null);
          }
        }

        nextStep();
      } catch (error) {
        console.error('Error saving form data:', error);
        setIsError('Failed to process the data. Please try again.');
      }
    },
    [
      selectedTab,
      isUpdateMode,
      currentScreeningType,
      job?.job_id,
      candidate?.candidate_id,
      screeningType,
      setIsError,
      getCandidateFollowUpQuestions,
      setFollowUpQuestions,
      setInterviewTitle,
      setInterviewDescription,
      setQuestions,
      setDeadline,
      setDeadlineTime,
      setTimezone,
      setSessionName,
      setScreeningType,
      setClient,
      setJob,
      setCandidate,
      nextStep
    ]
  );

  const handleBack = useCallback(() => {
    resetForm();
    resetStore();
    navigate(-1);
  }, [resetForm, resetStore, navigate]);

  const handleDirectCandidateSelect = useCallback(
    (candidate: Candidate) => {
      setValue('directCandidate', candidate);
    },
    [setValue]
  );

  // Enhanced handlers with setValue integration
  const enhancedClientSelection = useCallback(
    (selectedClient?: Client) => {
      handleClientSelection(selectedClient, setValue);
    },
    [handleClientSelection, setValue]
  );

  const enhancedJobSelection = useCallback(
    (selectedJob?: JobBasicInfo & { candidates?: CandidateWithJobContext[] }) => {
      handleJobSelection(selectedJob, setValue);
    },
    [handleJobSelection, setValue]
  );

  // Tab change handler
  const handleTabChange = useCallback(
    (details: { value: string }) => {
      const newTab = details.value as 'hierarchical' | 'direct';
      setSelectedTab(newTab);
      clearErrors(['client', 'job', 'candidate', 'directCandidate']);
      clearIsError();
    },
    [setSelectedTab, clearErrors, clearIsError]
  );

  // Derived state
  const selectedClient = watch('client');
  const selectedJob = watch('job');
  const selectedDirectCandidate = watch('directCandidate');

  return (
    <Box
      w="100%"
      border="1px solid"
      borderColor="neutrals.200"
      borderRadius="12px"
      p="32px"
      bg="background.secondary"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap="24px">
          <Stack gap="0">
            <Text fontSize="20px" color="neutrals.900" fontWeight="bold">
              Session Details
            </Text>
            <Text fontSize="18px" color="neutrals.500">
              Fill in session details.
            </Text>
          </Stack>

          {/* Mode Selection Tabs - Only show in create mode without fixed data */}
          {!isUpdateMode && !fixedCandidate && !fixedJobInterview ? (
            <Tabs.Root value={selectedTab} onValueChange={handleTabChange}>
              <Tabs.List mb="32px">
                <Tabs.Trigger value="hierarchical" fontSize="16px">
                  Job Interview
                </Tabs.Trigger>
                <Tabs.Trigger value="direct" fontSize="16px">
                  Resume Screening
                </Tabs.Trigger>
              </Tabs.List>

              <Stack gap="24px">
                <DeadlineSection
                  control={control}
                  register={register}
                  watch={watch}
                  setValue={setValue}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  isLoadingCandidate={isLoadingCandidate}
                  options={options}
                  parseTimezone={parseTimezone}
                  validationRules={validationRules}
                />
                <SessionNameSection
                  register={register}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  isLoadingCandidate={isLoadingCandidate}
                />
              </Stack>

              <Tabs.Content value="hierarchical">
                <HierarchicalSelection
                  control={control}
                  validationRules={validationRules}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  isLoadingCandidate={isLoadingCandidate}
                  isUpdateMode={isUpdateMode}
                  isFixedJobInterview={fixedJobInterview}
                  clients={clients}
                  jobs={jobs}
                  candidates={candidates}
                  selectedClient={selectedClient}
                  selectedJob={selectedJob}
                  watch={watch}
                  handleClientSelection={enhancedClientSelection}
                  handleJobSelection={enhancedJobSelection}
                />
              </Tabs.Content>

              <Tabs.Content value="direct">
                <DirectCandidateTab
                  selectedCandidateId={selectedDirectCandidate?.candidate_id}
                  onSelectCandidate={handleDirectCandidateSelect}
                />
              </Tabs.Content>
            </Tabs.Root>
          ) : (
            <Stack gap="24px">
              <DeadlineSection
                control={control}
                register={register}
                watch={watch}
                setValue={setValue}
                errors={errors}
                isSubmitting={isSubmitting}
                isLoadingCandidate={isLoadingCandidate}
                options={options}
                parseTimezone={parseTimezone}
                validationRules={validationRules}
              />
              <SessionNameSection
                register={register}
                errors={errors}
                isSubmitting={isSubmitting}
                isLoadingCandidate={isLoadingCandidate}
              />

              {/* Show fields for update mode or fixed job interview */}
              {isUpdateMode ? (
                <UpdateModeFields
                  screeningType={screeningType}
                  client={client}
                  job={job as Job}
                  candidate={candidate}
                  errors={errors}
                />
              ) : fixedJobInterview ? (
                <HierarchicalSelection
                  control={control}
                  validationRules={validationRules}
                  errors={errors}
                  isSubmitting={isSubmitting}
                  isLoadingCandidate={isLoadingCandidate}
                  isUpdateMode={false}
                  isFixedJobInterview={fixedJobInterview}
                  clients={clients}
                  jobs={jobs}
                  candidates={candidates}
                  selectedClient={selectedClient}
                  selectedJob={selectedJob}
                  watch={watch}
                  handleClientSelection={enhancedClientSelection}
                  handleJobSelection={enhancedJobSelection}
                />
              ) : null}
            </Stack>
          )}
        </Stack>

        {noCandidates && (
          <Box color="error.500" fontSize="16px" mt="16px" role="alert">
            No candidates available for scheduling. Please add candidates to the pipeline first.
          </Box>
        )}

        {/* Error Message */}
        {isError && (
          <Box color="error.700" fontSize="16px" mt="16px" role="alert">
            {isError}
          </Box>
        )}

        {/* Submit Button */}
        <Flex justifyContent="flex-end" mt="64px" gap="24px">
          <Button
            p="12px 64px"
            fontSize="16px"
            border="1px solid"
            borderColor="primary.900"
            borderRadius="8px"
            bg="neutrals.0"
            color="primary.900"
            disabled={isSubmitting || isLoadingCandidate}
            onClick={handleBack}
            _hover={{ bg: 'neutrals.50' }}
          >
            Back
          </Button>
          <Button
            type="submit"
            p="12px 64px"
            fontSize="16px"
            borderRadius="8px"
            bg="primary.900"
            color="neutrals.0"
            disabled={isLoadingCandidate || noCandidates}
            loading={isSubmitting}
            _hover={{ bg: 'primary.700' }}
          >
            Next
          </Button>
        </Flex>
      </form>
    </Box>
  );
}
